import React, { useState, useEffect } from 'react';
import { X, User, Lock, ShieldCheck, Save, Type } from 'lucide-react';
import { AppUser, UserPermissions } from '../types';

interface UserFormProps {
  initialData?: any;
  onClose: () => void;
  onSave: (data: any) => void;
}

const defaultPermissions: UserPermissions = {
  dashboard: true,
  properties: true,
  tenants: true,
  invoices: true,
  expenses: true,
  maintenance: true,
  employees: true,
  reports: true,
  settings: true,
  userManagement: false
};

const UserForm: React.FC<UserFormProps> = ({ initialData, onClose, onSave }) => {
  const [formData, setFormData] = useState<any>({
    username: '',
    password: '',
    fullName: '',
    permissions: defaultPermissions
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        password: '', // Don't show password during edit
        permissions: initialData.permissions || defaultPermissions
      });
    } else {
      setFormData({
        username: '',
        password: '',
        fullName: '',
        permissions: defaultPermissions
      });
    }
    setErrors({});
  }, [initialData]);

  const togglePermission = (key: keyof UserPermissions) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [key]: !formData.permissions[key]
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, boolean> = {};
    if (!formData.username) newErrors.username = true;
    if (!formData.fullName) newErrors.fullName = true;
    if (!initialData && !formData.password) newErrors.password = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      alert("⚠️ يرجى ملء كافة الحقول المطلوبة (المميزة باللون الأحمر)");
      return;
    }
    onSave(formData);
  };

  const inputClass = "w-full bg-slate-50 border-2 border-slate-100 rounded-xl py-3 px-4 outline-none focus:border-fly-500 transition-all text-sm font-medium";
  const labelClass = "block text-xs font-black text-slate-500 mb-2 mr-1 uppercase tracking-wider";

  const permissionItems = [
    { key: 'dashboard', label: 'لوحة التحكم' },
    { key: 'properties', label: 'العقارات والوحدات' },
    { key: 'tenants', label: 'المستأجرين' },
    { key: 'invoices', label: 'الفواتير والتحصيل' },
    { key: 'expenses', label: 'المصروفات' },
    { key: 'maintenance', label: 'الصيانة' },
    { key: 'employees', label: 'إدارة الموظفين' },
    { key: 'reports', label: 'التقارير المالية' },
    { key: 'settings', label: 'الإعدادات' },
    { key: 'userManagement', label: 'إدارة المستخدمين (أدمن)' },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[96vh]">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-fly-50 p-2.5 rounded-xl">
              <ShieldCheck className="text-fly-600" size={20} />
            </div>
            <h2 className="text-xl font-black text-slate-800">{initialData ? 'تعديل صلاحيات مستخدم' : 'إضافة مستخدم جديد'}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <div className="space-y-8">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`${labelClass} ${errors.username ? 'text-rose-500' : ''}`}>إسم المستخدم (Username) *</label>
                <div className="relative">
                  <User className={`absolute right-4 top-1/2 -translate-y-1/2 ${errors.username ? 'text-rose-400' : 'text-slate-400'}`} size={18} />
                  <input 
                    required
                    autoComplete="off"
                    autoCapitalize="none"
                    className={`${inputClass} pr-12 ${errors.username ? 'border-rose-200 bg-rose-50 focus:border-rose-400' : ''}`}
                    value={formData.username}
                    onChange={e => {
                        setFormData({ ...formData, username: e.target.value });
                        if (errors.username) setErrors({ ...errors, username: false });
                    }}
                    placeholder="example_user"
                  />
                </div>
              </div>
              <div>
                <label className={`${labelClass} ${errors.password ? 'text-rose-500' : ''}`}>كلمة المرور {initialData ? '(اتركه فارغاً للحفاظ على القديمة)' : '*'}</label>
                <div className="relative">
                  <Lock className={`absolute right-4 top-1/2 -translate-y-1/2 ${errors.password ? 'text-rose-400' : 'text-slate-400'}`} size={18} />
                  <input 
                    type="password"
                    autoComplete="new-password"
                    className={`${inputClass} pr-12 ${errors.password ? 'border-rose-200 bg-rose-50 focus:border-rose-400' : ''}`}
                    value={formData.password}
                    onChange={e => {
                        setFormData({ ...formData, password: e.target.value });
                        if (errors.password) setErrors({ ...errors, password: false });
                    }}
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className={`${labelClass} ${errors.fullName ? 'text-rose-500' : ''}`}>إسم الموظف الكامل *</label>
              <div className="relative">
                <Type className={`absolute right-4 top-1/2 -translate-y-1/2 ${errors.fullName ? 'text-rose-400' : 'text-slate-400'}`} size={18} />
                <input 
                  required
                  autoComplete="off"
                  className={`${inputClass} pr-12 ${errors.fullName ? 'border-rose-200 bg-rose-50 focus:border-rose-400' : ''}`}
                  value={formData.fullName}
                  onChange={e => {
                      setFormData({ ...formData, fullName: e.target.value });
                      if (errors.fullName) setErrors({ ...errors, fullName: false });
                  }}
                  placeholder="محمد أحمد..."
                />
              </div>
            </div>

            {/* Permissions */}
            <div>
               <h3 className="text-sm font-black text-slate-800 mb-4 border-b pb-2">صلاحيات الوصول للوحات النظام</h3>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {permissionItems.map(item => (
                    <div 
                      key={item.key}
                      onClick={() => togglePermission(item.key as keyof UserPermissions)}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        formData.permissions[item.key] 
                        ? 'border-fly-100 bg-fly-50 text-fly-700' 
                        : 'border-slate-50 bg-slate-50 text-slate-400'
                      }`}
                    >
                      <span className="text-sm font-bold">{item.label}</span>
                      <div className={`w-10 h-6 rounded-full relative transition-colors ${formData.permissions[item.key] ? 'bg-fly-600' : 'bg-slate-200'}`}>
                         <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.permissions[item.key] ? 'right-5' : 'right-1'}`}></div>
                      </div>
                    </div>
                  ))}
               </div>
            </div>
          </div>
        </form>

        <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3 shrink-0">
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 bg-fly-600 text-white py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-fly-700 transition-all shadow-lg shadow-fly-600/20 active:scale-95"
          >
            <Save size={20} />
            حفظ المستخدم
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-8 bg-white text-slate-500 py-4 rounded-2xl font-bold border-2 border-slate-200 hover:bg-slate-50 transition-all active:scale-95"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
